import { CachedDataCollector } from "src/core/base/CachedDataCollector";
import { TrackerVariableType } from "src/core/model/TrackerVariableType";
import { CacheStrategy } from "src/core/model/VariableCollectionMetadata";
import { BrowserDetector } from "src/util/BrowserDetector";
import { Logger } from "src/util/Logger";

// --- Suspicious flags (from remote control monitoring) ---

export interface SuspiciousFlags {
  unnaturalMouseMoves: boolean;
  bigClipboardPaste: boolean;
  lowFPSDetected: boolean;
  delayedClickDetected: boolean;
}

// --- Environment baseline (persisted in localStorage) ---

interface EnvironmentBaseline {
  language: string | null;
  timezone: string | null;
  os: string | null;
  browser: string | null;
  firstSeen: number;
}

interface EnvironmentChangeFlags {
  languageChanged: boolean;
  timezoneChanged: boolean;
  osChanged: boolean;
  browserChanged: boolean;
}

// --- Risk metrics aggregates ---

interface RiskAggregates {
  noMouseMoveDurationMs: number;
  clickOnlyPattern: boolean;
  fastPageTransition: boolean;
  navigationVelocity: number;
  formFillAnomaly: boolean;
  formFillDurationMs: number;
  inputPasteRatio: number;
  redirectSource: string | null;
}

const BASELINE_KEY_PREFIX = "formica_baseline_";

/**
 * Collects security & risk metrics.
 * Mirrors Android SecurityCollector + merges tracker-web_current risk-metrics + remote control detection.
 *
 * Responsibilities:
 * 1. Remote control suspicion monitoring (mouse, clipboard, FPS, delayed clicks)
 * 2. Risk metrics aggregation (mouse movement, click patterns, form fill, navigation, paste ratio)
 * 3. Environment baseline tracking (language, timezone, OS, browser)
 * 4. Composite remote access score calculation
 */
export class SecurityCollector extends CachedDataCollector {
  private suspiciousFlags: SuspiciousFlags = {
    unnaturalMouseMoves: false,
    bigClipboardPaste: false,
    lowFPSDetected: false,
    delayedClickDetected: false,
  };

  // Risk metrics state
  private lastMouseMoveTs = Date.now();
  private mouseMoveCounterWindow = 0;
  private clickCounterWindow = 0;
  private pageViewTimestamps: number[] = [];
  private pasteCharCount = 0;
  private typedCharCount = 0;
  private lastPageViewTs: number | null = null;
  private lastFormInteractionStart: number | null = null;
  private lastFormFieldCount = 0;
  private lastFormDurationMs = 0;
  private formFillAnomaly = false;
  private fastPageTransition = false;
  private redirectSource: string | null = null;
  private lastUrl: string | null = null;

  // Remote control monitoring state
  private lastMouseX: number | null = null;
  private lastMouseY: number | null = null;
  private unnaturalMoveCount = 0;
  private lowFPSCount = 0;
  private lastFrameTs = 0;
  private fpsRafId: number | null = null;
  private listenersAttached = false;

  // Environment baseline
  private baseline: EnvironmentBaseline | null = null;
  private envChangeFlags: EnvironmentChangeFlags | null = null;
  private envChangeScore = 0;

  // Tenant/device context (set during init)
  private tenant = "";
  private deviceId = "";

  collect(): Map<TrackerVariableType, unknown> {
    const data = new Map<TrackerVariableType, unknown>();
    const agg = this.getAggregates();

    // Suspicious flags
    this.collectWithCache(data, TrackerVariableType.SUSPICIOUS_ACTIVITY, CacheStrategy.FAST, () => ({
      ...this.suspiciousFlags,
    }));

    // Risk aggregates
    this.collectIfNeeded(data, TrackerVariableType.NO_MOUSE_MOVE_DURATION_MS, () => agg.noMouseMoveDurationMs);
    this.collectIfNeeded(data, TrackerVariableType.CLICK_ONLY_PATTERN, () => agg.clickOnlyPattern);
    this.collectIfNeeded(data, TrackerVariableType.FAST_PAGE_TRANSITION, () => agg.fastPageTransition);
    this.collectIfNeeded(data, TrackerVariableType.NAVIGATION_VELOCITY, () => agg.navigationVelocity);
    this.collectIfNeeded(data, TrackerVariableType.FORM_FILL_ANOMALY, () => agg.formFillAnomaly);
    this.collectIfNeeded(data, TrackerVariableType.FORM_FILL_DURATION_MS, () => agg.formFillDurationMs);
    this.collectIfNeeded(data, TrackerVariableType.INPUT_PASTE_RATIO, () => agg.inputPasteRatio);
    this.collectIfNeeded(data, TrackerVariableType.REDIRECT_DETECTED, () => agg.redirectSource);

    // Environment change
    this.collectIfNeeded(data, TrackerVariableType.ENVIRONMENT_CHANGE_FLAGS, () =>
      this.envChangeFlags ? { ...this.envChangeFlags } : null,
    );
    this.collectIfNeeded(data, TrackerVariableType.ENVIRONMENT_CHANGE_SCORE, () => this.envChangeScore);

    // Composite scores
    const headlessCount = BrowserDetector.getHeadlessIndicatorCount();
    const remoteAccessScore = this.calcRemoteAccessScore(headlessCount);

    this.collectIfNeeded(data, TrackerVariableType.REMOTE_ACCESS_SCORE, () => remoteAccessScore);
    this.collectIfNeeded(data, TrackerVariableType.REMOTE_ACCESS_FLAGS, () => ({
      ...this.suspiciousFlags,
      clickOnlyPattern: agg.clickOnlyPattern,
      headlessIndicatorCount: headlessCount,
      environmentMismatch: this.envChangeScore > 0,
    }));

    return data;
  }

  // === Initialization ===

  /**
   * Start monitoring. Called once during SDK initialization.
   */
  init(tenant: string, deviceId: string): void {
    this.tenant = tenant;
    this.deviceId = deviceId;
    this.attachListeners();
    this.ensureBaseline();
  }

  // === Page view tracking ===

  registerPageView(): void {
    const now = Date.now();
    this.fastPageTransition = this.lastPageViewTs !== null && now - this.lastPageViewTs < 1500;
    this.lastPageViewTs = now;
    this.pageViewTimestamps.push(now);

    // Keep last 5 minutes only
    const cutoff = now - 5 * 60 * 1000;
    this.pageViewTimestamps = this.pageViewTimestamps.filter((t) => t >= cutoff);

    this.detectRedirect();
  }

  // === Risk score ===

  getRemoteAccessScore(): number {
    return this.calcRemoteAccessScore(BrowserDetector.getHeadlessIndicatorCount());
  }

  resetRollingWindow(): void {
    this.mouseMoveCounterWindow = 0;
    this.clickCounterWindow = 0;
  }

  // === Environment baseline ===

  updateEnvironmentDiff(): void {
    if (!this.baseline) return;
    const current = this.getCurrentEnvironment();
    this.envChangeFlags = this.diffEnvironment(this.baseline, current);
    this.envChangeScore = this.calcEnvironmentChangeScore(this.envChangeFlags);
  }

  // === Internal ===

  private getAggregates(): RiskAggregates {
    const now = Date.now();
    const noMouseMoveDurationMs = now - this.lastMouseMoveTs;
    const clickOnlyPattern =
      noMouseMoveDurationMs > 10_000 && this.clickCounterWindow >= 3 && this.mouseMoveCounterWindow < 2;
    const navigationVelocity = this.pageViewTimestamps.length / 5;
    const inputPasteRatio = this.computePasteRatio();

    return {
      noMouseMoveDurationMs,
      clickOnlyPattern,
      fastPageTransition: this.fastPageTransition,
      navigationVelocity,
      formFillAnomaly: this.formFillAnomaly,
      formFillDurationMs: this.lastFormDurationMs,
      inputPasteRatio,
      redirectSource: this.redirectSource,
    };
  }

  private computePasteRatio(): number {
    const total = this.pasteCharCount + this.typedCharCount;
    if (total === 0) return 0;
    return this.pasteCharCount / total;
  }

  private calcRemoteAccessScore(headlessIndicatorCount: number): number {
    const agg = this.getAggregates();
    let score = 0;
    if (this.suspiciousFlags.unnaturalMouseMoves) score += 0.2;
    if (this.suspiciousFlags.bigClipboardPaste) score += 0.15;
    if (this.suspiciousFlags.lowFPSDetected) score += 0.15;
    if (this.suspiciousFlags.delayedClickDetected) score += 0.1;
    if (agg.clickOnlyPattern) score += 0.15;
    if (headlessIndicatorCount > 0) score += Math.min(headlessIndicatorCount * 0.05, 0.25);
    if (this.envChangeScore > 0) score += 0.2;
    return Math.min(1, score);
  }

  // === Remote control suspicion listeners ===

  private attachListeners(): void {
    if (this.listenersAttached) return;
    this.listenersAttached = true;

    // Mouse move — risk metrics + unnatural detection
    window.addEventListener("mousemove", (e: MouseEvent) => {
      this.lastMouseMoveTs = Date.now();
      this.mouseMoveCounterWindow++;

      if (this.lastMouseX !== null && this.lastMouseY !== null) {
        const dx = Math.abs(e.clientX - this.lastMouseX);
        const dy = Math.abs(e.clientY - this.lastMouseY);
        if (dx > 100 || dy > 100 || (dx === 0 && dy === 0)) this.unnaturalMoveCount++;
        if (this.unnaturalMoveCount > 5) this.suspiciousFlags.unnaturalMouseMoves = true;
      }
      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;
    });

    // Click counter
    document.addEventListener("click", () => {
      this.clickCounterWindow++;

      // Delayed click detection
      const start = performance.now();
      setTimeout(() => {
        const delay = performance.now() - start;
        if (delay > 150) this.suspiciousFlags.delayedClickDetected = true;
      }, 0);
    });

    // Paste detection
    document.addEventListener("paste", (e: Event) => {
      const ce = e as ClipboardEvent;
      const text = ce.clipboardData?.getData("text") ?? "";
      this.pasteCharCount += text.length;
      if (text.length > 5000) this.suspiciousFlags.bigClipboardPaste = true;
    });

    // Keydown counting
    document.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key.length === 1) this.typedCharCount++;
    });

    // Form tracking
    document.addEventListener("focusin", (e: FocusEvent) => {
      const form = (e.target as HTMLElement)?.closest("form");
      if (!form) return;
      if (this.lastFormInteractionStart === null) {
        this.lastFormInteractionStart = Date.now();
        this.lastFormFieldCount = form.querySelectorAll("input,textarea,select").length;
      }
    });

    document.addEventListener("submit", (e: SubmitEvent) => {
      if (!e.target) return;
      if (this.lastFormInteractionStart) {
        this.lastFormDurationMs = Date.now() - this.lastFormInteractionStart;
        this.formFillAnomaly = this.detectFormAnomaly(this.lastFormDurationMs, this.lastFormFieldCount);
      }
      this.lastFormInteractionStart = null;
    });

    // FPS monitoring
    this.lastFrameTs = performance.now();
    const monitorFPS = (): void => {
      const now = performance.now();
      const delta = now - this.lastFrameTs;
      if (delta > 250) this.lowFPSCount++;
      if (this.lowFPSCount > 10) this.suspiciousFlags.lowFPSDetected = true;
      this.lastFrameTs = now;
      this.fpsRafId = requestAnimationFrame(monitorFPS);
    };
    this.fpsRafId = requestAnimationFrame(monitorFPS);
  }

  private detectFormAnomaly(durationMs: number, fieldCount: number): boolean {
    if (fieldCount === 0) return false;
    const adaptiveThreshold = Math.min(fieldCount * 120, 1500);
    return durationMs < adaptiveThreshold;
  }

  private detectRedirect(): void {
    try {
      const currentUrl = window.location.href;
      if (this.lastUrl && this.lastUrl !== currentUrl) {
        if (document.referrer && document.referrer !== currentUrl) {
          try {
            const referrerUrl = new URL(document.referrer);
            const currentUrlObj = new URL(currentUrl);
            this.redirectSource = referrerUrl.origin !== currentUrlObj.origin ? referrerUrl.origin : "internal";
          } catch {
            this.redirectSource = document.referrer;
          }
        } else if (!document.referrer) {
          this.redirectSource = "direct";
        } else {
          this.redirectSource = null;
        }
      } else {
        this.redirectSource = null;
      }
      this.lastUrl = currentUrl;
    } catch {
      this.redirectSource = null;
    }
  }

  // === Baseline ===

  private ensureBaseline(): void {
    try {
      const key = `${BASELINE_KEY_PREFIX}${this.tenant}_${this.deviceId}`;
      const raw = localStorage.getItem(key);
      if (raw) {
        this.baseline = JSON.parse(raw) as EnvironmentBaseline;
      } else {
        const current = this.getCurrentEnvironment();
        this.baseline = { ...current, firstSeen: Date.now() };
        localStorage.setItem(key, JSON.stringify(this.baseline));
      }
      this.updateEnvironmentDiff();
    } catch {
      Logger.warn("localStorage unavailable for environment baseline");
    }
  }

  private getCurrentEnvironment(): Omit<EnvironmentBaseline, "firstSeen"> {
    const browser = BrowserDetector.getBrowser();
    const os = BrowserDetector.getOS();
    return {
      language: BrowserDetector.getLanguage(),
      timezone: BrowserDetector.getTimezone(),
      os: `${os.name} ${os.version}`,
      browser: `${browser.name} ${browser.version}`,
    };
  }

  private diffEnvironment(
    baseline: EnvironmentBaseline,
    current: Omit<EnvironmentBaseline, "firstSeen">,
  ): EnvironmentChangeFlags {
    return {
      languageChanged: baseline.language !== current.language,
      timezoneChanged: baseline.timezone !== current.timezone,
      osChanged: baseline.os !== current.os,
      browserChanged: baseline.browser !== current.browser,
    };
  }

  private calcEnvironmentChangeScore(flags: EnvironmentChangeFlags): number {
    let score = 0;
    if (flags.languageChanged) score += 0.25;
    if (flags.timezoneChanged) score += 0.25;
    if (flags.osChanged) score += 0.2;
    if (flags.browserChanged) score += 0.3;
    return Math.min(1, score);
  }

  /** Cleanup FPS monitor on termination. */
  destroy(): void {
    if (this.fpsRafId !== null) {
      cancelAnimationFrame(this.fpsRafId);
      this.fpsRafId = null;
    }
  }
}
